/**
 * API签名工具
 * 使用HMAC-SHA256算法生成API请求签名
 * 与后端的signatureVerify.middleware.ts对应
 */

// 签名配置（从环境变量读取，需要与后端.env中的配置一致）
const SIGNATURE_CONFIG = {
  APP_ID: import.meta.env.VITE_APP_ID || 'coze_voice_chat',
  APP_SECRET: import.meta.env.VITE_APP_SECRET || 'your_secret_key_here',
};

/**
 * 生成随机nonce
 */
function generateNonce(length = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * 计算请求签名
 * @param params 请求参数（Query或Body）
 * @param timestamp 时间戳
 * @param nonce 随机字符串
 */
export async function calculateSignature(
  params: Record<string, any>,
  timestamp: number,
  nonce: string
): Promise<string> {
  // 1. 按ASCII码升序排序参数
  const sortedKeys = Object.keys(params).sort();
  const paramString = sortedKeys
    .filter(key => params[key] !== undefined && params[key] !== null)
    .map(k => `${k}=${String(params[k])}`)
    .join('&');

  // 2. 追加签名参数
  const signString = `${paramString}&timestamp=${timestamp}&nonce=${nonce}&app_secret=${SIGNATURE_CONFIG.APP_SECRET}`;

  console.log('[API签名] 签名字符串:', signString.substring(0, 100) + '...');

  // 3. 使用Web Crypto API进行HMAC-SHA256签名
  const encoder = new TextEncoder();
  const keyData = encoder.encode(SIGNATURE_CONFIG.APP_SECRET);
  const messageData = encoder.encode(signString);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    messageData
  );

  // 4. 转换为hex字符串
  const hashArray = Array.from(new Uint8Array(signature));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

/**
 * 为API请求添加签名头
 * @param url 请求URL
 * @param method 请求方法
 * @param body 请求体（可选）
 * @param queryParams 查询参数（可选）
 */
export async function signRequest(
  _url: string,
  _method: string,
  body?: Record<string, any>,
  queryParams?: Record<string, any>
): Promise<Record<string, string>> {
  // 1. 合并所有参数
  const allParams: Record<string, any> = {
    ...queryParams,
    ...body,
  };

  // 2. 生成时间戳和nonce
  const timestamp = Math.floor(Date.now() / 1000);
  const nonce = generateNonce();

  // 3. 计算签名
  const signature = await calculateSignature(allParams, timestamp, nonce);

  // 4. 返回签名头
  return {
    'X-App-Id': SIGNATURE_CONFIG.APP_ID,
    'X-Timestamp': String(timestamp),
    'X-Nonce': nonce,
    'X-Signature': signature,
  };
}

/**
 * 从URL和body中提取参数
 */
export function extractParams(_url: string, body?: any): Record<string, any> {
  const params: Record<string, any> = {};

  // 提取body参数
  if (body && typeof body === 'object') {
    Object.assign(params, body);
  }

  return params;
}
